import { useCallback, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Check, CheckCheck, ClipboardCheck, Search, Users, X } from 'lucide-react'

import { useLocalAuth } from '@/auth/LocalAuthContext'
import { canEditModule } from '@/auth/modulePermission'
import DialogShell from '@/components/crm/DialogShell'
import { dataReview, invalidateAfterDecision, refusalOf } from '@/services/dataReview'
import {
  confirmationKeys,
  confirmationRefusal,
  dealConfirmation,
  invalidateAfterConfirmation,
  type ConfirmationAction,
  type ConfirmationQuestion,
  type ConfirmationRevision,
  type ConfirmationRow,
} from '@/services/dealConfirmation'
import { ingestionService } from '@/services/ingestion'
import { ConfirmationViewQueueDialog } from './ConfirmationViewQueue'
import { ConfirmationViewRow, ROW_GRID, type RowController } from './ConfirmationViewRow'
import {
  CONFIRMATION_FILTERS,
  buildEdit,
  clearAction,
  confirmActions,
  confirmablePending,
  filterCount,
  progressPercent,
  progressText,
  reviewBanner,
  visibleSections,
  type ConfirmationFilter,
} from './ConfirmationViewModel'

/**
 * Confirmation view — ONE editable screen for a deal's populated and missing
 * data, and for who has vouched for each figure.
 *
 * A deal appears on the pipeline the moment its package is read, and everything
 * on it is the engine's reading. This lists every field of the catalogue —
 * what was found AND what was not — with where each value came from, and lets a
 * person confirm it as it stands, correct it, reject it, or fill in what is
 * missing. A value stays PENDING until a person does; the platform's own
 * writes (auto-promotion, derivation) never count. Who confirmed and when is
 * recorded per field, in the deal's field trail.
 *
 * Nothing is applied that somebody did not press: "Confirm all visible
 * pending" names how many it is about to confirm and asks first, and a bulk
 * write is all-or-nothing — one refusal leaves the deal untouched.
 *
 * Conflicts are not decided here by a new route: the rival values are the Data
 * Intelligence Review's own options, and picking one calls its decide route.
 */

export interface ConfirmationViewProps {
  loanId: string
  /** Shown as a close button when the view is hosted in a dialog. */
  onClose?: () => void
}

type Notice = { tone: 'ok' | 'error'; text: string } | null

export default function ConfirmationView({ loanId, onClose }: ConfirmationViewProps) {
  const queryClient = useQueryClient()
  const { localUser } = useLocalAuth()
  // Presentation only: the API refuses a write the account may not make, and an
  // account with no resolved profile is drawn as allowed.
  const mayEditAccount = canEditModule(localUser, 'intake') && canEditModule(localUser, 'pipeline')

  const viewQuery = useQuery({
    queryKey: confirmationKeys.view(loanId),
    queryFn: () => dealConfirmation.view(loanId),
  })
  const view = viewQuery.data
  const mayEdit = mayEditAccount && Boolean(view) && !view?.readOnly

  const [filter, setFilter] = useState<ConfirmationFilter>('all')
  const [query, setQuery] = useState('')
  const [showNotApplicable, setShowNotApplicable] = useState(false)
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [reasons, setReasons] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [notice, setNotice] = useState<Notice>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [clearing, setClearing] = useState<string | null>(null)
  const [bulk, setBulk] = useState<ConfirmationRow[] | null>(null)
  const [queueOpen, setQueueOpen] = useState(false)
  const [returning, setReturning] = useState(false)
  const [returnNote, setReturnNote] = useState('')
  // A deal opened from the manager queue, over this one.
  const [nested, setNested] = useState<string | null>(null)

  const sections = useMemo(
    () => (view ? visibleSections(view, filter, query, { showNotApplicable }) : []),
    [view, filter, query, showNotApplicable],
  )
  const confirmable = useMemo(() => confirmablePending(sections), [sections])
  const shown = sections.reduce((n, s) => n + s.rows.length, 0)

  const refresh = useCallback(
    () => invalidateAfterConfirmation(queryClient, loanId),
    [queryClient, loanId],
  )

  /** Send actions through the one bulk route and say what happened. */
  const apply = useCallback(
    async (actions: ConfirmationAction[], key: string): Promise<boolean> => {
      setBusy(key)
      setNotice(null)
      setErrors({})
      try {
        const result = await dealConfirmation.record(loanId, actions)
        await refresh()
        const settled = new Set(actions.map((a) => a.field))
        const without = (current: Record<string, string>) =>
          Object.fromEntries(Object.entries(current).filter(([field]) => !settled.has(field)))
        setDrafts(without)
        setReasons(without)
        setClearing(null)
        const parts = [`${result.changedN} recorded under your name`]
        if (result.unchangedN > 0) parts.push(`${result.unchangedN} already as shown`)
        if (result.review.enabled && result.review.state === 'awaiting_manager' && result.changedN > 0) {
          parts.push('now awaiting manager approval')
        }
        if (result.derivation?.error) parts.push(result.derivation.error)
        setNotice({ tone: result.derivation?.error ? 'error' : 'ok', text: `${parts.join(' · ')}.` })
        return true
      } catch (error) {
        const refusal = confirmationRefusal(error)
        setErrors(Object.fromEntries(refusal.errors.map((e) => [e.field, e.message])))
        setNotice({ tone: 'error', text: refusal.message })
        // A value that changed since it was shown: show the person what it is now.
        if (refusal.status === 409) await refresh()
        return false
      } finally {
        setBusy(null)
      }
    },
    [loanId, refresh],
  )

  const controller: RowController = {
    mayEdit,
    busy,
    draft: (field) => drafts[field],
    reason: (field) => reasons[field] ?? '',
    error: (field) => errors[field],
    clearing,
    setDraft: (field, value) =>
      setDrafts((current) => {
        const next = { ...current }
        if (value === undefined) delete next[field]
        else next[field] = value
        return next
      }),
    setReason: (field, value) => setReasons((current) => ({ ...current, [field]: value })),
    confirm: (row) => void apply(confirmActions([row]), row.field),
    save: (row) => {
      const built = buildEdit(row, drafts[row.field] ?? '', reasons[row.field] ?? '')
      if (!built.ok) {
        setErrors({ [row.field]: built.error })
        return
      }
      void apply([built.action], row.field)
    },
    askClear: setClearing,
    clear: (row) => void apply([clearAction(row)], row.field),
    pick: async (question: ConfirmationQuestion, index: number) => {
      const option = question.options[index]
      if (!option) return
      setBusy(question.id)
      setNotice(null)
      try {
        await dataReview.decide({
          itemId: question.id,
          verb: 'correct',
          value: option.value,
          provenance: option.provenance,
        })
        await Promise.all([invalidateAfterDecision(queryClient, loanId), refresh()])
        setNotice({ tone: 'ok', text: 'Value chosen — recorded under your name.' })
      } catch (error) {
        setNotice({ tone: 'error', text: refusalOf(error).message })
      } finally {
        setBusy(null)
      }
    },
    applyRevision: async (revision: ConfirmationRevision, applyIt: boolean) => {
      setBusy(`${revision.ingestionId}:${revision.field}`)
      setNotice(null)
      try {
        await ingestionService.applyToLoan(
          revision.ingestionId,
          applyIt ? [revision.field] : [],
          undefined,
          applyIt ? [] : [revision.field],
        )
        await Promise.all([invalidateAfterDecision(queryClient, loanId), refresh()])
        setNotice({
          tone: 'ok',
          text: applyIt ? 'The later email’s value was applied — recorded under your name.' : 'The deal keeps its value — recorded under your name.',
        })
      } catch (error) {
        setNotice({ tone: 'error', text: refusalOf(error).message })
      } finally {
        setBusy(null)
      }
    },
  }

  const decideReview = async (kind: 'approve' | 'return') => {
    setBusy(`review:${kind}`)
    setNotice(null)
    try {
      if (kind === 'approve') await dealConfirmation.approve(loanId)
      else await dealConfirmation.returnToConfirmer(loanId, returnNote)
      await refresh()
      setReturning(false)
      setReturnNote('')
      setNotice({
        tone: 'ok',
        text: kind === 'approve' ? 'Approved — recorded under your name.' : 'Returned with your note.',
      })
    } catch (error) {
      setNotice({ tone: 'error', text: confirmationRefusal(error).message })
    } finally {
      setBusy(null)
    }
  }

  const banner = reviewBanner(view?.review)
  const title = view?.property ?? 'Deal'

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="confirmation-view">
      {/* One header: what this is, how far along, and what can be done in bulk. */}
      <div className="flex flex-shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b border-amber-400/10 px-4 py-2.5">
        <ClipboardCheck className="h-4 w-4 shrink-0 text-amber-300" aria-hidden="true" />
        <div className="min-w-0">
          <div className="font-mono text-[9px] uppercase tracking-widest text-stone-500">Review &amp; confirm</div>
          <div className="truncate text-[13px] font-medium text-stone-100" title={title}>
            {title}
          </div>
        </div>
        {view ? (
          <div className="min-w-[180px] flex-1">
            <div className="flex items-baseline justify-between gap-2 text-[11px]">
              <span className="font-medium text-stone-200" data-testid="progress-text">
                {progressText(view.summary)}
              </span>
              <span className="text-[10px] text-stone-500">
                {view.summary.derivedN > 0 ? `${view.summary.derivedN} derived — confirm their inputs` : ''}
              </span>
            </div>
            <div
              className="mt-1 h-1 overflow-hidden rounded bg-stone-800"
              role="progressbar"
              aria-label="Confirmed"
              aria-valuenow={progressPercent(view.summary)}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div className="h-full bg-emerald-400/80" style={{ width: `${progressPercent(view.summary)}%` }} />
            </div>
            {view.summary.confirmedByLast ? (
              <div className="mt-0.5 text-[10px] text-stone-500">
                Last confirmed by {view.summary.confirmedByLast}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="flex-1" />
        )}
        <div className="flex items-center gap-2">
          {view?.review.enabled && view.review.canApprove ? (
            <button
              type="button"
              onClick={() => setQueueOpen(true)}
              className="btn-secondary inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px]"
            >
              <Users className="h-3 w-3" /> Manager queue
            </button>
          ) : null}
          {mayEdit ? (
            <button
              type="button"
              onClick={() => setBulk(confirmable)}
              disabled={confirmable.length === 0 || busy !== null}
              className="inline-flex items-center gap-1.5 rounded bg-amber-300 px-3 py-1.5 text-[11px] font-medium text-black disabled:opacity-40"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Confirm all visible pending ({confirmable.length})
            </button>
          ) : null}
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded p-1 text-stone-500 hover:text-stone-200"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>

      {view ? (
        <div className="flex flex-shrink-0 flex-wrap items-center gap-2 border-b border-white/[0.05] px-4 py-2">
          {CONFIRMATION_FILTERS.map((chip) => {
            const active = filter === chip.id
            return (
              <button
                key={chip.id}
                type="button"
                onClick={() => setFilter(chip.id)}
                aria-pressed={active}
                className={`inline-flex items-center gap-1.5 rounded border px-2 py-1 text-[10px] uppercase tracking-wide ${
                  active
                    ? 'border-amber-300/60 bg-amber-300/10 text-amber-200'
                    : 'border-stone-700 text-stone-400 hover:border-stone-500'
                }`}
              >
                {chip.label}
                <span className="tabular-nums text-stone-500">{filterCount(view.summary, chip.id)}</span>
              </button>
            )
          })}
          <label className="relative ml-auto">
            <Search className="pointer-events-none absolute left-2 top-1.5 h-3 w-3 text-stone-600" aria-hidden="true" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find a field or value"
              aria-label="Find a field or value"
              className="w-56 rounded border border-stone-700 bg-black/40 py-1 pl-7 pr-2 text-[11px] text-stone-100 outline-none placeholder:text-stone-600 focus:border-amber-300/50"
            />
          </label>
          {view.summary.notApplicableN > 0 ? (
            <label className="flex items-center gap-1.5 text-[10px] text-stone-500">
              <input
                type="checkbox"
                checked={showNotApplicable}
                onChange={(e) => setShowNotApplicable(e.target.checked)}
              />
              Show {view.summary.notApplicableN} that do not apply to this asset class
            </label>
          ) : null}
        </div>
      ) : null}

      {banner ? (
        <div
          role="status"
          data-testid="review-banner"
          className={`flex flex-shrink-0 flex-wrap items-center gap-2 border-b px-4 py-2 text-[11px] ${
            banner.tone === 'approved'
              ? 'border-emerald-400/15 bg-emerald-400/[0.05] text-emerald-200'
              : banner.tone === 'returned'
                ? 'border-red-400/20 bg-red-400/[0.05] text-red-200'
                : 'border-amber-300/15 bg-amber-300/[0.05] text-amber-200'
          }`}
        >
          <span>{banner.text}</span>
          {view?.review.state === 'awaiting_manager' && view.review.canApprove ? (
            <span className="ml-auto flex gap-1.5">
              <button
                type="button"
                onClick={() => void decideReview('approve')}
                disabled={busy !== null}
                className="rounded bg-emerald-400 px-2.5 py-1 text-[10px] font-medium text-black disabled:opacity-50"
              >
                Approve
              </button>
              <button
                type="button"
                onClick={() => setReturning(true)}
                disabled={busy !== null}
                className="rounded border border-amber-300/40 px-2.5 py-1 text-[10px] text-amber-200 disabled:opacity-50"
              >
                Return…
              </button>
            </span>
          ) : null}
        </div>
      ) : null}

      {notice ? (
        <div
          role={notice.tone === 'error' ? 'alert' : 'status'}
          data-testid="confirmation-notice"
          className={`flex flex-shrink-0 items-start gap-2 border-b px-4 py-2 text-[11px] ${
            notice.tone === 'error'
              ? 'border-red-400/20 bg-red-400/[0.06] text-red-200'
              : 'border-emerald-400/15 bg-emerald-400/[0.05] text-emerald-200'
          }`}
        >
          {notice.tone === 'error' ? (
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
          ) : (
            <Check className="mt-px h-3.5 w-3.5 shrink-0" />
          )}
          <span className="min-w-0 flex-1">{notice.text}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" className="shrink-0 text-stone-500 hover:text-stone-200">
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : null}

      {!mayEditAccount && view ? (
        <div className="flex-shrink-0 border-b border-white/[0.05] px-4 py-1.5 text-[10px] text-stone-500">
          You can read this deal’s confirmation state. Confirming or editing needs Intake and Pipeline edit access.
        </div>
      ) : null}
      {view?.readOnly ? (
        <div className="flex-shrink-0 border-b border-white/[0.05] px-4 py-1.5 text-[10px] text-stone-500">
          This is a demo-partition record and is read-only.
        </div>
      ) : null}

      {/* The table: one scroll area, no horizontal overflow. */}
      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin" data-testid="confirmation-rows">
        {viewQuery.isLoading ? (
          <p className="px-4 py-6 text-[12px] text-stone-500">Reading the deal…</p>
        ) : viewQuery.isError || !view ? (
          <div className="px-4 py-6 text-[12px] text-red-300">
            <p>The confirmation view could not be read. Nothing has been changed.</p>
            <button
              type="button"
              onClick={() => void viewQuery.refetch()}
              className="btn-secondary mt-2 px-3 py-1 text-[11px]"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            <div
              className={`${ROW_GRID} sticky top-0 z-10 border-b border-amber-400/10 bg-[#0b0b0b] px-3 py-1.5 font-mono text-[9px] uppercase tracking-widest text-stone-500`}
            >
              <div>Field</div>
              <div>Value</div>
              <div>Source</div>
              <div>Status</div>
              <div className="text-right">{mayEdit ? 'Actions' : ''}</div>
            </div>
            {shown === 0 ? (
              <p className="px-4 py-6 text-[12px] text-stone-500">
                {query.trim() || filter !== 'all'
                  ? 'No field matches this filter.'
                  : 'This deal has no catalog fields.'}
              </p>
            ) : (
              sections.map((section) => (
                <section key={section.group} aria-label={section.group}>
                  <div className="flex items-baseline justify-between border-b border-white/[0.05] bg-white/[0.02] px-3 py-1 text-[10px] uppercase tracking-widest text-amber-200/80">
                    <span>{section.group}</span>
                    <span className="font-mono text-[9px] text-stone-500">{section.rows.length}</span>
                  </div>
                  {section.rows.map((row) => (
                    <ConfirmationViewRow key={row.field} row={row} controller={controller} />
                  ))}
                </section>
              ))
            )}
          </>
        )}
      </div>

      {bulk ? (
        <DialogShell
          label="Confirm pending values"
          width="md"
          zIndex={140}
          onClose={() => setBulk(null)}
          footer={
            <>
              <button type="button" onClick={() => setBulk(null)} className="btn-secondary px-3 py-1.5 text-[11px]">
                Cancel
              </button>
              <button
                type="button"
                disabled={busy !== null || bulk.length === 0}
                onClick={async () => {
                  const ok = await apply(confirmActions(bulk), 'bulk')
                  if (ok) setBulk(null)
                }}
                className="rounded bg-amber-300 px-3 py-1.5 text-[11px] font-medium text-black disabled:opacity-50"
              >
                Confirm {bulk.length} {bulk.length === 1 ? 'value' : 'values'}
              </button>
            </>
          }
        >
          <div className="space-y-2 px-4 py-3 text-[12px] text-stone-300">
            <p>
              You are about to confirm <strong className="text-stone-100">{bulk.length}</strong>{' '}
              {bulk.length === 1 ? 'value' : 'values'} as they stand. Each is recorded as confirmed by you, with the
              time. No value is changed.
            </p>
            <ul className="max-h-48 space-y-0.5 overflow-y-auto rounded border border-white/[0.06] px-3 py-2 text-[11px] text-stone-400">
              {bulk.map((row) => (
                <li key={row.field} className="truncate">
                  {row.label}
                </li>
              ))}
            </ul>
            <p className="text-[10px] text-stone-500">
              If any of these has changed since you opened this screen, nothing is confirmed and you are told which.
            </p>
          </div>
        </DialogShell>
      ) : null}

      {returning ? (
        <DialogShell
          label="Return to the person who confirmed it"
          width="md"
          zIndex={140}
          onClose={() => setReturning(false)}
          footer={
            <>
              <button type="button" onClick={() => setReturning(false)} className="btn-secondary px-3 py-1.5 text-[11px]">
                Cancel
              </button>
              <button
                type="button"
                disabled={busy !== null || !returnNote.trim()}
                onClick={() => void decideReview('return')}
                className="rounded bg-amber-300 px-3 py-1.5 text-[11px] font-medium text-black disabled:opacity-50"
              >
                Return with note
              </button>
            </>
          }
        >
          <label className="block space-y-1 px-4 py-3 text-[11px] text-stone-400">
            What should they look at again? A return without a note is just a delay.
            <textarea
              value={returnNote}
              onChange={(e) => setReturnNote(e.target.value)}
              aria-label="Note for the person who confirmed it"
              rows={4}
              className="w-full rounded border border-stone-700 bg-black/40 px-2 py-1.5 text-[12px] text-stone-100 outline-none focus:border-amber-300/50"
            />
          </label>
        </DialogShell>
      ) : null}

      {queueOpen ? (
        <ConfirmationViewQueueDialog onClose={() => setQueueOpen(false)} onOpenDeal={(id) => setNested(id)} />
      ) : null}
      {nested ? <ConfirmationViewDialog loanId={nested} zIndex={170} onClose={() => setNested(null)} /> : null}
    </div>
  )
}

/** The view in a dialog — for the Intake record, the Deal Inspector and the
 *  dossier header. The view carries its own header, so the dialog adds none. */
export function ConfirmationViewDialog({
  loanId,
  onClose,
  zIndex = 130,
}: {
  loanId: string
  onClose: () => void
  zIndex?: number
}) {
  return (
    <DialogShell label="Review and confirm" width="landscape" zIndex={zIndex} onClose={onClose} header={<></>}>
      <ConfirmationView loanId={loanId} onClose={onClose} />
    </DialogShell>
  )
}
